import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, debounceTime, filter, map } from 'rxjs';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { AppState } from 'src/app/store/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PERMISSIONS_DEFAULT_SORT_COLUMN, PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION, PERMISSIONS_PAGE_DEFAULT_SIZE, PERMISSIONS_PAGE_SIZE_OPTIONS } from 'src/app/conf/permissions';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Permission, EPermissionStatus, PermittedCommand, PermittedThs } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectLoading } from 'src/app/store/user-file/user-file.selector';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectPermissions, selectPermissionsPaging, selectPermissionsAdded, selectPermissionsEdited, selectPermissionsDeleted, selectPermissionsPinResetted, selectPermittedCommands, selectPermittedThs } from 'src/app/store/permissions/permissions.selector';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { TranslateService } from '@ngx-translate/core';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { PermissionDialogComponent } from '../../permission-dialog/permission.dialog.component';
import { PermissionEmailDialogComponent } from '../../permission-email-dialog/permission-email.dialog.component';
import { CommonCustomConfirmDialogComponent } from 'src/app/modules/shared/components/common-custom-confirm-dialog/common-custom-confirm.dialog.component';

@Component({
	selector: 'app-permissions-list-filters',
	templateUrl: './permissions-list-filters.component.html',
	providers: [ConfirmationService, DialogService]
})

export class PermissionsListFiltersComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	permissionsLoading$: Observable<boolean | undefined>;
	permissionsLoadingSub: Subscription | undefined;

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	permissions$: Observable<Permission[] | undefined>;
	permissionsSub: Subscription | undefined;

	paging$: Observable<ItemsPagination | undefined>;
	pagingSub: Subscription | undefined;

	added$: Observable<boolean | undefined>;
	addedSub: Subscription | undefined;

	edited$: Observable<boolean | undefined>;
	editedSub: Subscription | undefined;

	deleted$: Observable<boolean | undefined>;
	deletedSub: Subscription | undefined;

	pinResetted$: Observable<boolean | undefined>;
	pinResettedSub: Subscription | undefined;

	permissionsLoading: boolean;
	permissions: Permission[];
	site: Site;

	filterForm: FormGroup;
	filter: '';
	activeFilter: true;
	readyFilter: true;
	expiredFilter: true;
	createdFilter: true;

	filterSub: Subscription | undefined;
	activeFilterSub: Subscription | undefined;
	readyFilterSub: Subscription | undefined;
	expiredFilterSub: Subscription | undefined;
	createdFilterSub: Subscription | undefined;

	paging: PaginationParams = {
		size: PERMISSIONS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: PERMISSIONS_DEFAULT_SORT_COLUMN,
		order: PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = PERMISSIONS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = PERMISSIONS_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	ref: DynamicDialogRef | undefined;

	permittedCommands: PermittedCommand[] = [];
	permittedCommands$: Observable<PermittedCommand[] | undefined>;
	permittedCommandsSub: Subscription | undefined;

	permittedThs: PermittedThs[] = [];
	permittedThs$: Observable<PermittedThs[] | undefined>;
	permittedThsSub: Subscription | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	addPermissionConfDialog = false;

	expiredPermissionsCount: number = 0;

	// Design
	screenWidth: number;
	COLUMN_TITLE_MIN_WD = 647;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;
	MIN_WD_COL4_MTMA = StandardDesignParameters.MIN_WD_COL4_MTMA;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.permissionsLoading$ = this.store.select(selectLoading);
		this.site$ = this.store.select(selectCurrentSite);
		this.permissions$ = this.store.select(selectPermissions);
		this.paging$ = this.store.select(selectPermissionsPaging);
		this.added$ = this.store.select(selectPermissionsAdded);
		this.edited$ = this.store.select(selectPermissionsEdited);
		this.deleted$ = this.store.select(selectPermissionsDeleted);
		this.pinResetted$ = this.store.select(selectPermissionsPinResetted);
		this.permittedCommands$ = this.store.select(selectPermittedCommands);
		this.permittedThs$ = this.store.select(selectPermittedThs);
	}

	ngOnInit(): void {
		this.filterForm = this.formBuilder.group({
			searchFilter: [''], activeFilter: true,
			readyFilter: true, expiredFilter: true, createdFilter: true
		});

		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.permissionsLoadingSub?.unsubscribe();
		this.permissionsSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.pagingSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.deletedSub?.unsubscribe();
		this.pinResettedSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.activeFilterSub?.unsubscribe();
		this.readyFilterSub?.unsubscribe();
		this.expiredFilterSub?.unsubscribe();
		this.createdFilterSub?.unsubscribe();
		this.permittedCommandsSub?.unsubscribe();
		this.permittedThsSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.permissionsLoadingSub = this.permissionsLoading$?.subscribe(
			(permissionsLoading) => {
				this.permissionsLoading = permissionsLoading;
			});

		this.permittedCommandsSub = this.permittedCommands$.subscribe(permittedCommands => {
			this.permittedCommands = permittedCommands;
		});
		this.permissionsSub = this.permissions$?.subscribe(
			(permissions) => {
				this.permissions = permissions;
				this.countExpiredPermissions();
			}
		);
		this.siteSub = this.site$.pipe(filter(s => !!s)).subscribe((site) => {
			this.site = site;
		});
		this.permittedThsSub = this.permittedThs$.subscribe(permittedThs => {
			this.permittedThs = permittedThs;
		});

		this.filterSub = this.filterForm.controls['searchFilter'].valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
		});

		this.activeFilterSub = this.filterForm.controls['activeFilter'].valueChanges.subscribe(value => {
			this.activeFilter = value;
		});

		this.readyFilterSub = this.filterForm.controls['readyFilter'].valueChanges.subscribe(value => {
			this.readyFilter = value;
		});

		this.expiredFilterSub = this.filterForm.controls['expiredFilter'].valueChanges.subscribe(value => {
			this.expiredFilter = value;
		});

		this.createdFilterSub = this.filterForm.controls['createdFilter'].valueChanges.subscribe(value => {
			this.createdFilter = value;
		});
	}

	countExpiredPermissions() {
		this.expiredPermissionsCount = this.permissions.filter(perm => perm.Status === EPermissionStatus.Expired).length;
	}

	expired(permission: Permission): boolean {
		return permission.Status === EPermissionStatus.Expired;
	}

	anyPossibleCommand(): boolean {
		return this.permittedCommands?.length > 0 || this.permittedThs?.length > 0;
	}

	addPermission(): void {
		this.addPermissionConfDialog = true;
		if (this.site?.Timezone === '') { // add permission not available due to site timezone
			this.dialogService.open(CommonCustomConfirmDialogComponent, {
				header: this.translate.instant('ADD_TIMEZONE_SITE'),
				contentStyle: { overflow: 'auto' },
				data: {
					icon: 'cm-alert',
					row1: 'ADD_TIMEZONE_SITE_WARNING',
					visibleAcceptButton: false,
					labelRejectButton: 'CLOSE',
				}
			});
		} else { // timezone valued
			if (this.anyPossibleCommand()) { // add permission available
				this.store.dispatch(PermissionActions.resetPermissionForm());
				this.ref = this.dialogService.open(PermissionDialogComponent, {
					header: this.translate.instant('ADD_PERMISSION'),
					width: '70vw',
					breakpoints: { '1600px': '98vw' },
					contentStyle: { overflow: 'auto' },
					data: {}
				});
			} else { // no commands available
				this.acceptButtonStyle = "p-button-primary";
				this.rejectButtonStyle = "p-button-secondary";
				this.confirmationService.confirm({
					header: this.translate.instant('WARNING'),
					message: this.translate.instant('FUNCTIONALITY_UNAVAILABLE'),
					acceptLabel: this.translate.instant('OK'),
					rejectLabel: this.translate.instant('CLOSE'),
					icon: 'pi cm-alert',
					reject: () => {
					}
				});
			}
		}
		this.addPermissionConfDialog = false;
	}

	deleteExpired(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_ALL_EXPIRES'),
			message: this.translate.instant('DELETE_PERMISSION_EXPIRED_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.deletePermissionExpired({ siteId: this.site.Id }));
			},
			reject: () => {
			}
		});
	}

	isFinalUsers(): boolean {
		return this.site !== undefined && this.site !== null && !this.site.CurrentlyOwned;
	}

	customizeEmail(): void {
		this.store.dispatch(PermissionActions.resetPermissionTemplateForm());
		this.ref = this.dialogService.open(PermissionEmailDialogComponent, {
			header: this.translate.instant('CUSTOMIZE_EMAIL'),
			width: '80vw',
			breakpoints: { '1220px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {}
		});
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onTablet(): boolean {
		return StandardPageLayout.onTablet(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	col6HeaderPage(position: string) : string {
		return StandardPageLayout.col6HeaderPage(this.screenWidth, position);
	}
}